import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import PublicLayout from "@/components/public/PublicLayout";
import HomePage from "@/pages/public/HomePage";
import BrandsPage from "@/pages/public/BrandsPage";
import ClothingPage from "@/pages/public/ClothingPage";
import MaterialsGuidePage from "@/pages/public/MaterialsGuidePage";
import ContactPage from "@/pages/public/ContactPage";
import MethodologyPage from "@/pages/public/MethodologyPage";
import DynamicCmsPage from "@/pages/public/DynamicCmsPage";
import GdprConfirmPage from "@/pages/public/GdprConfirmPage";
import LegacyCmsRedirect from "@/pages/public/LegacyCmsRedirect";
import BrandDetailPage from "@/pages/public/BrandDetailPage";
import ClothingDetailPage from "@/pages/public/ClothingDetailPage";
import MaterialDetailPage from "@/pages/public/MaterialDetailPage";
import CertificationsPage from "@/pages/public/CertificationsPage";
import CertificationDetailPage from "@/pages/public/CertificationDetailPage";
import BrandCertificationsPage from "@/pages/public/BrandCertificationsPage";
import BrandCertificationDetailPage from "@/pages/public/BrandCertificationDetailPage";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBrandsList from "@/pages/admin/AdminBrandsList";
import AdminBrandEdit from "@/pages/admin/AdminBrandEdit";
import AdminCrawlerResults from "@/pages/admin/AdminCrawlerResults";
import AdminMaterialsList from "@/pages/admin/AdminMaterialsList";
import AdminMaterialEdit from "@/pages/admin/AdminMaterialEdit";
import AdminCertificationsList from "@/pages/admin/AdminCertificationsList";
import AdminCertificationEdit from "@/pages/admin/AdminCertificationEdit";
import AdminBrandCertificationsList from "@/pages/admin/AdminBrandCertificationsList";
import AdminBrandCertificationEdit from "@/pages/admin/AdminBrandCertificationEdit";
import AdminClothingTypesList from "@/pages/admin/AdminClothingTypesList";
import AdminClothingList from "@/pages/admin/AdminClothingList";
import AdminClothingEdit from "@/pages/admin/AdminClothingEdit";
import AdminCrawlRuns from "@/pages/admin/AdminCrawlRuns";
import AdminReports from "@/pages/admin/AdminReports";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminCmsList from "@/pages/admin/AdminCmsList";
import AdminCmsEdit from "@/pages/admin/AdminCmsEdit";
import AdminNavBuilder from "@/pages/admin/AdminNavBuilder";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPageSeo from "@/pages/admin/AdminPageSeo";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminErrorLog from "@/pages/admin/AdminErrorLog";
import AdminGdpr from "@/pages/admin/AdminGdpr";
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton duration={Infinity} />
        <Routes>
          {/* Public */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/brands/:slug" element={<BrandDetailPage />} />
            <Route path="/clothing" element={<ClothingPage />} />
            <Route path="/clothing/:slug" element={<ClothingDetailPage />} />
            <Route path="/materials-guide" element={<MaterialsGuidePage />} />
            <Route path="/materials/:slug" element={<MaterialDetailPage />} />
            <Route path="/materials-certifications" element={<CertificationsPage />} />
            <Route path="/materials-certifications/:slug" element={<CertificationDetailPage />} />
            <Route path="/brand-certifications" element={<BrandCertificationsPage />} />
            <Route path="/brand-certifications/:slug" element={<BrandCertificationDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/gdpr/confirm/:token" element={<GdprConfirmPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/p/:slug" element={<LegacyCmsRedirect />} />
            {/* Catch-all root slug: brand pages take precedence above; otherwise try CMS. */}
            <Route path="/:slug" element={<DynamicCmsPage />} />
          </Route>

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/brands" element={<AdminBrandsList />} />
            <Route path="/admin/brands/new" element={<AdminBrandEdit />} />
            <Route path="/admin/brands/:id" element={<AdminBrandEdit />} />
            <Route path="/admin/brands/:id/crawler-results" element={<AdminCrawlerResults />} />
            <Route path="/admin/materials" element={<AdminMaterialsList />} />
            <Route path="/admin/materials/new" element={<AdminMaterialEdit />} />
            <Route path="/admin/materials/:id" element={<AdminMaterialEdit />} />
            <Route path="/admin/materials-certifications" element={<AdminCertificationsList />} />
            <Route path="/admin/materials-certifications/new" element={<AdminCertificationEdit />} />
            <Route path="/admin/materials-certifications/:id" element={<AdminCertificationEdit />} />
            <Route path="/admin/brand-certifications" element={<AdminBrandCertificationsList />} />
            <Route path="/admin/brand-certifications/new" element={<AdminBrandCertificationEdit />} />
            <Route path="/admin/brand-certifications/:id" element={<AdminBrandCertificationEdit />} />
            <Route path="/admin/clothing-types" element={<AdminClothingTypesList />} />
            <Route path="/admin/clothing-items" element={<AdminClothingList />} />
            <Route path="/admin/clothing-items/new" element={<AdminClothingEdit />} />
            <Route path="/admin/clothing-items/:id" element={<AdminClothingEdit />} />
            <Route path="/admin/crawl-runs" element={<AdminCrawlRuns />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/cms-pages" element={<AdminCmsList />} />
            <Route path="/admin/cms-pages/new" element={<AdminCmsEdit />} />
            <Route path="/admin/cms-pages/:id" element={<AdminCmsEdit />} />
            <Route path="/admin/navigation" element={<AdminNavBuilder />} />
            <Route path="/admin/page-seo" element={<AdminPageSeo />} />
            <Route path="/admin/errors" element={<AdminErrorLog />} />
            <Route path="/admin/gdpr" element={<AdminGdpr />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
